/**
 * HAR MLS Market Areas — all Greater Houston cities in the HAR MLS coverage
 * area, grouped for the UI. Used by:
 *  - /properties main filter dropdown
 *  - /mls-listings city filter dropdown
 *  - Home search bar
 *  - Home quick-search chips
 *
 * These are actual `city` values as stored in Trestle/HAR MLS records, so
 * they can be passed straight to `GET /api/mls/listings?city=...`.
 */

export const HAR_MARKET_GROUPS = [
  {
    label: "Central Houston",
    cities: [
      "Houston",
      "Bellaire",
      "West University Place",
      "Southside Place",
      "Hedwig Village",
      "Hunters Creek Village",
      "Piney Point Village",
      "Bunker Hill Village",
      "Spring Valley Village",
      "Hilshire Village",
      "Jacinto City",
    ],
  },
  {
    label: "Houston Sub-Markets",
    cities: [
      "Alief",
      "Sharpstown",
      "Gulfton",
      "Westchase",
      "Westbury",
      "Braeswood",
      "Meyerland",
      "Willowbrook",
      "Copperfield",
      "Northwest Houston",
      "Champions",
      "Memorial",
      "Galleria",
      "Uptown",
      "Museum District",
      "Medical Center",
      "Rice Village",
      "Montrose",
      "Midtown",
      "Downtown Houston",
      "EaDo",
      "Third Ward",
      "Fifth Ward",
      "The Heights",
      "Garden Oaks",
      "Oak Forest",
      "Timbergrove",
      "Spring Branch",
      "River Oaks",
      "Tanglewood",
      "Afton Oaks",
      "Briargrove",
      "Greenway Plaza",
      "Upper Kirby",
      "Neartown",
      "Sunny Side",
      "Clear Lake City",
      "Kingwood Area",
    ],
  },
  {
    label: "West Houston / Katy Area",
    cities: [
      "Katy",
      "Fulshear",
      "Cinco Ranch",
      "Cypress",
      "Hockley",
      "Waller",
      "Brookshire",
      "Pattison",
      "Simonton",
    ],
  },
  {
    label: "Southwest / Fort Bend",
    cities: [
      "Sugar Land",
      "Missouri City",
      "Richmond",
      "Rosenberg",
      "Stafford",
      "Meadows Place",
      "Needville",
      "Fresno",
      "Arcola",
      "Beasley",
      "Kendleton",
      "Orchard",
      "Thompsons",
      "Pleak",
    ],
  },
  {
    label: "South / Pearland Area",
    cities: [
      "Pearland",
      "Friendswood",
      "Manvel",
      "Alvin",
      "Brookside Village",
      "Iowa Colony",
      "Angleton",
      "Lake Jackson",
      "Freeport",
      "Clute",
      "West Columbia",
      "Sweeny",
      "Brazoria",
      "Danbury",
      "Rosharon",
    ],
  },
  {
    label: "Southeast / Bay Area",
    cities: [
      "League City",
      "Clear Lake",
      "Clear Lake Shores",
      "Webster",
      "Kemah",
      "Seabrook",
      "Nassau Bay",
      "El Lago",
      "Taylor Lake Village",
      "Dickinson",
      "La Marque",
      "Texas City",
      "Galveston",
      "Bacliff",
      "San Leon",
      "Hitchcock",
      "Santa Fe",
    ],
  },
  {
    label: "East / Ship Channel",
    cities: [
      "Pasadena",
      "Deer Park",
      "La Porte",
      "Baytown",
      "Channelview",
      "Highlands",
      "Crosby",
      "Mont Belvieu",
      "Anahuac",
      "Winnie",
      "Beach City",
      "Cove",
    ],
  },
  {
    label: "North / Woodlands Area",
    cities: [
      "The Woodlands",
      "Spring",
      "Tomball",
      "Conroe",
      "Magnolia",
      "Willis",
      "Montgomery",
      "New Caney",
      "Porter",
      "Splendora",
      "Cleveland",
      "Shenandoah",
      "Oak Ridge North",
      "Panorama Village",
      "Cut and Shoot",
      "Pinehurst",
      "Plantersville",
    ],
  },
  {
    label: "Northeast Houston",
    cities: [
      "Humble",
      "Kingwood",
      "Atascocita",
      "Huffman",
      "Dayton",
      "Liberty",
      "Livingston",
      "Onalaska",
    ],
  },
  {
    label: "Northwest Houston",
    cities: [
      "Jersey Village",
      "Klein",
      "Cy-Fair",
      "Prairie View",
      "Hempstead",
      "Bellville",
      "Sealy",
      "Wallis",
    ],
  },
];

// Flat, alphabetized city list — used by dropdowns
export const HAR_MARKET_CITIES = Array.from(
  new Set(HAR_MARKET_GROUPS.flatMap((g) => g.cities))
).sort();

// Houston sub-markets are neighborhoods within the City of Houston.
// In HAR MLS these records have `city = "Houston"` and the market-area name
// lives in subdivision/address text. Rather than fuzzy text search, we map
// each sub-market to its known HAR zip codes so `?zip_code=...` filtering
// returns geographically-accurate results.
export const HOUSTON_SUB_MARKET_ZIPS = {
  "Alief": ["77072", "77082", "77083", "77099"],
  "Sharpstown": ["77036", "77074"],
  "Gulfton": ["77081"],
  "Westchase": ["77042", "77063", "77082"],
  "Westbury": ["77035"],
  "Braeswood": ["77025", "77030", "77096"],
  "Meyerland": ["77035", "77096"],
  "Willowbrook": ["77070", "77064", "77086"],
  "Copperfield": ["77095", "77433"],
  "Northwest Houston": ["77040", "77041", "77043", "77064", "77065", "77070", "77086", "77088", "77092"],
  "Champions": ["77069", "77070", "77068"],
  "Memorial": ["77024", "77079", "77007"],
  "Galleria": ["77056", "77057"],
  "Uptown": ["77027", "77056"],
  "Museum District": ["77004", "77006"],
  "Medical Center": ["77030"],
  "Rice Village": ["77005"],
  "Montrose": ["77006", "77019"],
  "Midtown": ["77002", "77004"],
  "Downtown Houston": ["77002", "77010"],
  "EaDo": ["77003"],
  "Third Ward": ["77004"],
  "Fifth Ward": ["77020"],
  "The Heights": ["77007", "77008", "77009", "77018"],
  "Garden Oaks": ["77018"],
  "Oak Forest": ["77018", "77092"],
  "Timbergrove": ["77008"],
  "Spring Branch": ["77043", "77055", "77080"],
  "River Oaks": ["77019", "77027"],
  "Tanglewood": ["77056", "77057"],
  "Afton Oaks": ["77027"],
  "Briargrove": ["77057"],
  "Greenway Plaza": ["77027", "77098"],
  "Upper Kirby": ["77098"],
  "Neartown": ["77006", "77019"],
  "Sunny Side": ["77033", "77051"],
  "Clear Lake City": ["77058", "77062"],
  "Kingwood Area": ["77339", "77345", "77346"],
};

// Houston sub-markets are neighborhoods within the City of Houston.
// (Kept for backwards-compat with any code that still consults the Set.)
export const HOUSTON_SUB_MARKETS = new Set(Object.keys(HOUSTON_SUB_MARKET_ZIPS));

/**
 * Given a market-area name selected in the UI, return the MLS query params.
 * For real cities → `city=Katy`. For Houston sub-markets → `zip_codes=77072,77082,...`
 * (falls back to `q=<name>` if no zip mapping exists).
 */
export const marketAreaToParams = (name) => {
  if (!name || name === "All") return {};
  if (HOUSTON_SUB_MARKETS.has(name)) {
    const zips = HOUSTON_SUB_MARKET_ZIPS[name];
    if (zips?.length) return { zip_codes: zips.join(",") };
    return { q: name };
  }
  return { city: name };
};

// Curated "quick-search chips" — the most searched market areas
// (used below the Home hero search bar)
export const HAR_QUICK_CHIPS = [
  "Houston",
  "Katy",
  "Sugar Land",
  "The Woodlands",
  "Cypress",
  "Spring",
  "Pearland",
  "Fulshear",
  "Missouri City",
  "Kingwood",
  "Bellaire",
  "West University Place",
  "League City",
  "Friendswood",
  "Tomball",
  "Conroe",
];

// Slug <-> city name conversion (keep in sync with sitemap generator in server.py)
export const citySlug = (name) =>
  name.toLowerCase().replace(/\s+/g, "-").replace(/[,.]/g, "");

export const cityFromSlug = (slug) => {
  const norm = (slug || "").toLowerCase();
  return HAR_MARKET_CITIES.find((c) => citySlug(c) === norm) || null;
};
